import { memo } from "react";

import { InView, type IntersectionOptions } from "react-intersection-observer";

import { Breadcrumbs } from "@components/Breadcrumbs";
import { VideoModal } from "@react/sections/ModalProvider/VideoModal";
import { AnchorButtons } from "@react/sections/ProductHero/AnchorButtons/";
import { BrandValueProps } from "@react/sections/ProductHero/BrandValueProps";
import { Footnotes } from "@react/sections/ProductHero/Footnotes/Footnotes.data";
import { GiftWithPurchase } from "@react/sections/ProductHero/GiftWithPurchase";
import { ProductBridge } from "@react/sections/ProductHero/ProductBridge";
import { ProductForm } from "@react/sections/ProductHero/ProductForm";
import { ProductGalleryCarousel } from "@react/sections/ProductHero/ProductGalleryCarousel";
import { ProductGalleryGrid } from "@react/sections/ProductHero/ProductGalleryGrid";
import { ProductHeroAccordion } from "@react/sections/ProductHero/ProductHeroAccordion/ProductHeroAccordion.data";
import { ProductIntro } from "@react/sections/ProductHero/ProductIntro/ProductIntro.data";
import { ProductOverview } from "@react/sections/ProductHero/ProductOverview";
import { ProductSelector } from "@react/sections/ProductHero/ProductSelector/";
import { ProductValueProps } from "@react/sections/ProductHero/ProductValueProps";
import { StickyProductBar } from "@react/sections/ProductHero/StickyProductBar";
import { UGCVideoCarousel } from "@react/sections/ProductHero/UGCVideoCarousel/";
import {
  type ProductHeroSettings,
  type OPProduct,
  type ThemeMode,
} from "@root/tablescape/state";
import { SizeGuideModal } from "@sections/ModalProvider/SizeGuideModal";
import { type TTailwindString, tw } from "classnames";

const styles = {
  base: tw(
    "bg-background-primary",
    "box-border",
    "pb-16",
    "mg:pb-24",
    "mg:pt-12"
  ),
  container: tw(
    "mx-auto",
    "max-w-screen-md",
    "mg:grid",
    "mg:max-w-screen-xl",
    "mg:grid-cols-[auto_464px]" as TTailwindString,
    "mg:grid-rows-[auto_auto_1fr]" as TTailwindString,
    "mg:gap-x-[116px]" as TTailwindString,
    "mg:pl-12",
    "mg:pr-12"
  ),
  galleryDesktop: tw("hidden", "mg:block"),
  galleryMobile: tw("mg:hidden"),
  breadcrumbsWrapperDesktop: tw("mb-3", "hidden", "mg:block"),
  gallery: tw("mg:row-span-2", "mg:row-start-2"),
  details: tw(
    "box-border",
    "px-6",
    "pt-4",
    "mg:row-start-2",
    "mg:px-0",
    "mg:pt-0"
  ),
  intro: tw("mb-4"),
  footnotes: tw("mt-2", "mg:mt-3"),
  variantSelection: tw(
    "pb-6",
    "border-0",
    "border-b",
    "border-solid",
    "border-divider",
    "mg:border-b-0",
    "mg:pb-4"
  ),
  breadcrumbsWrapperMobile: tw("mb-3", "mg:hidden"),
  productBridge: tw("mb-4", "mg:mb-6"),
  additionalInfo: tw(
    "box-border",
    "border-0",
    "px-6",
    "pt-8",
    "mg:row-start-3",
    "mg:border-t",
    "mg:border-solid",
    "mg:border-divider",
    "mg:px-0"
  ),
  productValuePropsMobile: tw("mt-12", "mg:mt-0", "mg:hidden"),
};

type Props = {
  brandValueProps: ProductHeroSettings["brandValueProps"];
  breadcrumbs: ProductHeroSettings["breadcrumbs"];
  breadcrumbsLabel: string;
  closeAriaLabel: string;
  content: ProductHeroSettings["productOverview"]["content"];
  formatBtnText: {
    available: string;
    soldOut: string;
    waitlisted: string;
  };
  giftWithPurchase: {
    ourGiftForYou: string;
  };
  heading: ProductHeroSettings["productOverview"]["heading"];
  homeLabel: string;
  inViewProps: IntersectionOptions;
  isStickyProductBarEnabled: boolean;
  linkAriaLabel: string;
  linkReadLess: string;
  linkReadMore: string;
  product: OPProduct;
  productBridgeLabel: string;
  productBridgeLabelCtaHandler: (() => void) | undefined;
  ratingHero: {
    textReadMoreReviews: string;
    textReviews: string;
  };
  selectOption: string;
  showComboSelectorExperience: boolean;
  showCompareAtPriceValueFootnote: boolean;
  showReviewsFootnote: boolean;
  theme: ThemeMode;
  videoLabel: string;
};

const ProductHeroBaseViewBase = ({
  brandValueProps,
  breadcrumbs,
  breadcrumbsLabel,
  content,
  formatBtnText,
  giftWithPurchase,
  heading,
  homeLabel,
  inViewProps,
  isStickyProductBarEnabled,
  linkAriaLabel,
  linkReadLess,
  linkReadMore,
  product,
  productBridgeLabel,
  productBridgeLabelCtaHandler,
  ratingHero,
  selectOption,
  showComboSelectorExperience,
  showCompareAtPriceValueFootnote,
  showReviewsFootnote,
  theme,
  videoLabel,
}: Props) => {
  return (
    <InView {...inViewProps}>
      {({ inView: addToCartButtonInView, ref: addToCartButtonRef }) => (
        <>
          {isStickyProductBarEnabled && (
            <StickyProductBar addToCartButtonInView={addToCartButtonInView} />
          )}
          <div className={styles.base} data-theme={theme}>
            <div className={styles.container}>
              {breadcrumbs.length > 0 && (
                <div className={styles.breadcrumbsWrapperDesktop}>
                  <Breadcrumbs
                    breadcrumbsLabel={breadcrumbsLabel}
                    homeLabel={homeLabel}
                    items={breadcrumbs}
                  />
                </div>
              )}
              <div className={styles.gallery}>
                <div className={styles.galleryDesktop}>
                  <ProductGalleryGrid videoLabel={videoLabel} />
                </div>
                <div className={styles.galleryMobile}>
                  <ProductGalleryCarousel />
                </div>
              </div>
              <div className={styles.details} id="product-details">
                <div className={styles.intro}>
                  <ProductIntro />
                </div>
                <div className={styles.productBridge}>
                  <ProductBridge
                    {...(productBridgeLabelCtaHandler && {
                      labelCtaText: productBridgeLabel,
                      onLabelCtaClick: productBridgeLabelCtaHandler,
                    })}
                    linkAriaLabel={linkAriaLabel}
                  />
                  {productBridgeLabelCtaHandler && (
                    <SizeGuideModal product={product} />
                  )}
                </div>
                <div
                  className={styles.variantSelection}
                  ref={addToCartButtonRef}
                >
                  {showComboSelectorExperience && product.productSelectorId ? (
                    <ProductSelector
                      formatBtnText={formatBtnText}
                      id={product.productSelectorId}
                      selectOption={selectOption}
                    />
                  ) : (
                    <ProductForm formatBtnText={formatBtnText} />
                  )}
                  {(showReviewsFootnote || showCompareAtPriceValueFootnote) && (
                    <div className={styles.footnotes}>
                      <Footnotes
                        showCompareAtPriceValueFootnote={
                          showCompareAtPriceValueFootnote
                        }
                        showReviewsFootnote={showReviewsFootnote}
                      />
                    </div>
                  )}
                </div>
                <GiftWithPurchase
                  giftWithPurchase={giftWithPurchase}
                  linkReadLess={linkReadLess}
                  linkReadMore={linkReadMore}
                />
              </div>
              <div className={styles.additionalInfo}>
                {breadcrumbs.length > 0 && (
                  <div className={styles.breadcrumbsWrapperMobile}>
                    <Breadcrumbs
                      breadcrumbsLabel={breadcrumbsLabel}
                      homeLabel={homeLabel}
                      items={breadcrumbs}
                    />
                  </div>
                )}
                <ProductOverview content={content} heading={heading} />
                <BrandValueProps valueProps={brandValueProps} />
                <UGCVideoCarousel />
                <ProductHeroAccordion rating={ratingHero} />
                <AnchorButtons />
                <div className={styles.productValuePropsMobile}>
                  <ProductValueProps />
                </div>
              </div>
            </div>
            <VideoModal />
          </div>
        </>
      )}
    </InView>
  );
};

export const ProductHeroBaseView = memo(ProductHeroBaseViewBase);
